import { DateEx } from '../../../../../../functions/date-ex';
import { MedicalResponse } from './medical-response';

const RISK_THRESHOLDS = {
  lowRiskMax: 40,
  midRangeMax: 60,
} as const;

const RISK_CATEGORIES = {
  low: { category: 'low-risk', color: 'green' },
  mid: { category: 'mid-range', color: 'yellow' },
  high: { category: 'at-risk', color: 'red' },
} as const;

const PERCENTAGE_WORD_MAP = {
  10: 'Ten',
  20: 'Twenty',
  30: 'Thirty',
  40: 'Fourty',
  50: 'Fifty',
  60: 'Sixty',
  70: 'Seventy',
  80: 'Eighty',
  90: 'Ninty',
} as const;

export interface ICDCode {
  id: number;
  code: string;
  group: string;
  description: string;
  isPrimary: boolean;
  position: number;
  midRangeDays: number | null;
  midRangeDate: string | null;
  atRiskDays: number | null;
  atRiskDate: string | null;

  // RTW fields (only for primary ICD in WC claims)
  rtwBestPractice?: string;
  rtwDuration?: string;
}

export interface ReturnToWorkDay {
  label: string;
  value: number;
  percentage: number;
  riskCategory: 'low-risk' | 'mid-range' | 'at-risk';
  color: 'green' | 'yellow' | 'red';
}

export interface RiskAnalysis {
  midRangeDays: number;
  midRangeDate: string;
  atRiskDays: number;
  atRiskDate: string;
}

export interface EffectiveDate {
  description: string;
  value: string;
  formattedDate: string;
}

export interface MedicalUIData {
  effectiveDate: string;
  formattedEffectiveDate: string;
  primaryICD: ICDCode;
  secondaryICDs: ICDCode[];
  allICDs: ICDCode[];
  returnToWorkDays: ReturnToWorkDay[];
  riskAnalysis: RiskAnalysis;
}

export class MedicalUIModelFactory {
  private static getRiskInfo(percentage: number) {
    if (percentage <= RISK_THRESHOLDS.lowRiskMax) return RISK_CATEGORIES.low;
    if (percentage <= RISK_THRESHOLDS.midRangeMax) return RISK_CATEGORIES.mid;
    return RISK_CATEGORIES.high;
  }

  static createReturnToWorkDays(record: MedicalResponse): ReturnToWorkDay[] {
    const percentages = Object.keys(PERCENTAGE_WORD_MAP).map(Number);

    return percentages.map((percentage) => {
      const word =
        PERCENTAGE_WORD_MAP[percentage as keyof typeof PERCENTAGE_WORD_MAP];
      const fieldName =
        `daysAwayFromWork${word}Percent` as keyof MedicalResponse;
      const value = Number(record?.[fieldName]) || 0;
      const riskInfo = this.getRiskInfo(percentage);

      return {
        label: `${percentage}%`,
        value,
        percentage,
        riskCategory: riskInfo.category,
        color: riskInfo.color,
      } as ReturnToWorkDay;
    });
  }

  static createRiskAnalysis(record: MedicalResponse): RiskAnalysis {
    return {
      midRangeDays: record.midRangeDays || 0,
      midRangeDate: record.midRangeDate?.toString() || '',
      atRiskDays: record.atRiskDays || 0,
      atRiskDate: record.atRiskDate?.toString() || '',
    };
  }

  static createMedicalUIData(record: MedicalResponse): MedicalUIData {
    const primaryICD: ICDCode = {
      id: 1,
      code: record?.primaryICD9 || '',
      group: record?.group || '',
      description: record?.description || '',
      isPrimary: true,
      position: 1,
      midRangeDays: record?.midRangeDays || 0,
      midRangeDate: record?.midRangeDate?.toString() || '',
      atRiskDays: record?.atRiskDays || 0,
      atRiskDate: record?.atRiskDate?.toString() || '',
      rtwBestPractice: record?.rtwBestPractice,
      rtwDuration: record?.rtwDuration,
    };

    const returnToWorkDays = this.createReturnToWorkDays(record);
    const riskAnalysis = this.createRiskAnalysis(record);

    const result: MedicalUIData = {
      effectiveDate: record?.currentEffectiveDate?.toString() || '',
      formattedEffectiveDate: record?.currentEffectiveDate?.toString() || '',
      primaryICD,
      secondaryICDs: [],
      allICDs: [primaryICD],
      returnToWorkDays,
      riskAnalysis,
    };

    return result;
  }

  static createEffectiveDateOptions(
    rawResponse: MedicalResponse[],
  ): EffectiveDate[] {
    const uniqueDates = new Map<string, EffectiveDate>();

    rawResponse.forEach((record) => {
      if (this.hasValidEffectiveDate(record)) {
        const dateKey = record.currentEffectiveDate?.toString() || '';
        if (!uniqueDates.has(dateKey)) {
          uniqueDates.set(dateKey, this.buildEffectiveDateOption(record));
        }
      }
    });

    return this.sortEffectiveDatesByMostRecent(
      Array.from(uniqueDates.values()),
    );
  }

  private static hasValidEffectiveDate(record: MedicalResponse): boolean {
    return !!record.currentEffectiveDate;
  }

  private static buildEffectiveDateOption(
    record: MedicalResponse,
  ): EffectiveDate {
    const dateString = record.currentEffectiveDate?.toString() || '';
    return {
      description: DateEx.formatFullDate(new Date(dateString)),
      value: dateString,
      formattedDate: dateString,
    };
  }

  private static sortEffectiveDatesByMostRecent(
    dates: EffectiveDate[],
  ): EffectiveDate[] {
    return dates.sort(
      (a, b) => new Date(b.value).getTime() - new Date(a.value).getTime(),
    );
  }
}
